import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import './index.css';
import MapPanel from './components/MapPanel';
import InsightPanel from './components/InsightPanel';
import FeasibilityInsightPanel from './components/FeasibilityInsightPanel';
import ScenarioDock from './components/ScenarioDock';
import Rail from './components/Rail';
import CommandPalette from './components/CommandPalette';
import LoginGate from './components/LoginGate';
import { getTheme, toggleTheme } from './theme';
import MyReportsPanel from './components/MyReportsPanel';
import PipelinePanel from './components/PipelinePanel';
import PortfolioSetup from './components/PortfolioSetup';
import InvoicesPanel from './components/InvoicesPanel';
import AccountPanel from './components/AccountPanel';
import PredictivePanel from './components/PredictivePanel';
import AdminPanel from './components/AdminPanel';
import PurchaseModal from './components/PurchaseModal';
import FeasibilityReportPanel from './components/FeasibilityReportPanel';
import {
  scoresite, scoreUnlock, scoreFeasibility, scoreFeasibilityUnlock, getPaymentStatus,
  getCredits, getAccount, getPoiPreview, getBrandLocations, getBrandStats,
  refreshBrand, triggerBenchmark, getSites, createSite, downloadReportPdf,
  MOCK_RESULT, CATEGORIES,
} from './api';
import { supabase } from './supabase';

function useIsMobile(bp = 768) {
  const [m, setM] = useState(window.innerWidth < bp);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp - 1}px)`);
    const h = (e) => setM(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [bp]);
  return m;
}

const OVERLAYS = [
  { key: 'pipeline',   label: 'Pipeline' },
  { key: 'reports',    label: 'Reports' },
  { key: 'predictive', label: 'Predictive' },
  { key: 'invoices',   label: 'Invoices' },
];

export default function App() {
  // scenario state — the whole app re-scores off these
  const [pin, setPin]           = useState(null);          // {lat, lon}
  const [category, setCategory] = useState('');
  const [radiusKm, setRadiusKm] = useState(3.0);
  const [capture, setCapture]   = useState(15.0);
  const [mode, setMode]         = useState('quick');       // 'quick' | 'feasibility'

  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [pdfBusy, setPdfBusy]   = useState(false);

  // feasibility (shopping centre) lives alongside retail, switched by mode
  const [feasResult, setFeasResult]   = useState(null);
  const [feasLoading, setFeasLoading] = useState(false);
  const [feasError, setFeasError]     = useState(null);
  const [feasUnlocking, setFeasUnlocking] = useState(false);
  const [feasFull, setFeasFull]       = useState(null);   // {result, request, location} for fullscreen

  const [userEmail, setUserEmail] = useState(null);
  const [credits, setCredits]     = useState(null);
  const [scCredits, setScCredits] = useState(null);
  const [isAdmin, setIsAdmin]     = useState(false);
  const [userProducts, setUserProducts]     = useState(null);
  const [userCategories, setUserCategories] = useState(null);
  const [brandLocations, setBrandLocations] = useState([]);
  const [mapPois, setMapPois]     = useState([]);

  const [sites, setSites]         = useState([]);
  const [selSiteId, setSelSiteId] = useState(null);

  const [overlay, setOverlay]         = useState(null);    // pipeline | reports | invoices | account
  const [showPurchase, setShowPurchase] = useState(false);
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [themeMode, setThemeMode]     = useState(getTheme());
  const [toast, setToast]             = useState(null);

  const isMobile = useIsMobile();
  const skipScoreRef = useRef(false);   // set when loading a saved report
  const scoreSeq = useRef(0);           // drop stale auto-score responses
  const toastTimer = useRef(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  // ---------- ⌘K command palette ----------
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowPalette((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---------- auth ----------
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data?.user?.email || null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setUserEmail(s?.user?.email || null);
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  // ---------- account / credits / brand on login ----------
  const refreshSites = useCallback(() => {
    getSites().then(setSites).catch(() => {});
  }, []);

  useEffect(() => {
    if (!userEmail) return;
    const timer = setTimeout(() => {
      getCredits().then(d => {
        setCredits(d.credits);
        setIsAdmin(d.admin || false);
      }).catch(() => { setCredits(0); setIsAdmin(false); });
      getAccount().then(acc => {
        if (acc) {
          setUserProducts(acc.product_interests || null);
          setUserCategories(acc.retail_categories || null);
          setScCredits(acc.sc_credits ?? 0);
        }
      }).catch(() => {});
      getBrandStats().then(bs => {
        if (bs && bs.brand) {
          if (bs.total_stores > 0 && (bs.stores_scored || 0) === 0) {
            triggerBenchmark().catch(() => {});
          } else if (bs.total_stores === 0) {
            refreshBrand().catch(() => {});
          }
        }
      }).catch(() => {});
      getBrandLocations().then(setBrandLocations).catch(() => setBrandLocations([]));
      refreshSites();
    }, 500);
    return () => clearTimeout(timer);
  }, [userEmail, refreshSites]);

  // ---------- PayFast return ----------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('payment_id');
    const cancelled = params.get('payment_cancelled');
    if (cancelled) {
      window.history.replaceState({}, '', window.location.pathname);
      showToast('Payment was cancelled');
      return;
    }
    if (!paymentId) return;
    window.history.replaceState({}, '', window.location.pathname);
    (async () => {
      setLoading(true);
      try {
        const payment = await getPaymentStatus(paymentId);
        if (payment.status !== 'completed') {
          showToast('Payment is still processing — refresh in a moment.');
          return;
        }
        const p = payment.report_params;
        skipScoreRef.current = true;
        setPin({ lat: p.lat, lon: p.lon });
        setCategory(p.category);
        setRadiusKm(p.radius_km || 3);
        const data = await scoreUnlock({
          lat: p.lat, lon: p.lon, category: p.category,
          radius_km: p.radius_km, format: p.format, anchor: p.anchor,
          own_brand: p.own_brand, payment_id: paymentId,
        });
        setResult(data);
        refreshSites();
      } catch (err) {
        showToast('Payment received but report failed — contact support with your payment ID.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- auto-score: the core loop ----------
  useEffect(() => {
    if (mode !== 'quick' || !pin || !category || !userEmail) return;
    if (skipScoreRef.current) { skipScoreRef.current = false; return; }

    const seq = ++scoreSeq.current;
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      try {
        const [data, pois] = await Promise.all([
          scoresite({
            lat: pin.lat, lon: pin.lon, category,
            radius_km: radiusKm, site_id: `site-${Date.now()}`,
          }),
          getPoiPreview(pin.lat, pin.lon, category, radiusKm).catch(() => []),
        ]);
        if (seq !== scoreSeq.current) return;
        setResult(data);
        setMapPois(pois);
      } catch (err) {
        if (seq !== scoreSeq.current) return;
        setResult(null);
        setError(err.message || 'Scoring failed');
      } finally {
        if (seq === scoreSeq.current) setLoading(false);
      }
    }, 650);
    return () => clearTimeout(t);
  }, [pin, category, radiusKm, mode, userEmail]);

  // ---------- auto-score: feasibility (shopping centre) ----------
  useEffect(() => {
    if (mode !== 'feasibility' || !pin || !userEmail) return;
    if (skipScoreRef.current) { skipScoreRef.current = false; return; }

    const seq = ++scoreSeq.current;
    setFeasLoading(true);
    setFeasError(null);
    const t = setTimeout(async () => {
      try {
        const data = await scoreFeasibility({ lat: pin.lat, lon: pin.lon, radius_km: radiusKm });
        if (seq !== scoreSeq.current) return;
        setFeasResult(data);
      } catch (err) {
        if (seq !== scoreSeq.current) return;
        setFeasResult(null);
        setFeasError(err.message || 'Feasibility scoring failed');
      } finally {
        if (seq === scoreSeq.current) setFeasLoading(false);
      }
    }, 650);
    return () => clearTimeout(t);
  }, [pin, radiusKm, mode, userEmail]);

  // ---------- handlers ----------
  const handleMapClick = useCallback((loc) => {
    setSelSiteId(null);
    setPin(loc);
  }, []);

  const handleUnlock = async () => {
    if (!pin || !category) return;
    if (!isAdmin && !(credits > 0)) { setShowPurchase(true); return; }
    setUnlocking(true);
    try {
      const data = await scoreUnlock({
        lat: pin.lat, lon: pin.lon, category, radius_km: radiusKm,
        site_id: `site-${Date.now()}`,
      });
      setResult(data);
      if (!isAdmin) setCredits(c => Math.max(0, (c || 0) - 1));
      refreshSites();
    } catch (err) {
      if (err.status === 402) setShowPurchase(true);
      else showToast(err.message || 'Unlock failed');
    } finally {
      setUnlocking(false);
    }
  };

  const handleFeasibilityRun = async () => {
    if (!pin) { showToast('Drop a pin on the map first'); return; }
    setFeasLoading(true);
    setFeasError(null);
    try {
      const data = await scoreFeasibility({ lat: pin.lat, lon: pin.lon, radius_km: radiusKm });
      setFeasResult(data);
    } catch (err) {
      setFeasResult(null);
      setFeasError(err.message || 'Feasibility scoring failed');
    } finally {
      setFeasLoading(false);
    }
  };

  const handleFeasUnlock = async () => {
    if (!pin) return;
    if (!isAdmin && !(scCredits > 0)) { setShowPurchase(true); return; }
    setFeasUnlocking(true);
    try {
      const data = await scoreFeasibilityUnlock({ lat: pin.lat, lon: pin.lon, radius_km: radiusKm });
      setFeasResult(data);
      if (!isAdmin) setScCredits(c => Math.max(0, (c || 0) - 1));
      refreshSites();
    } catch (err) {
      if (err.status === 402) setShowPurchase(true);
      else showToast(err.message || 'Unlock failed');
    } finally {
      setFeasUnlocking(false);
    }
  };

  const handleSaveSite = async () => {
    if (!pin) return;
    try {
      const name = `Site · ${pin.lat.toFixed(4)}, ${pin.lon.toFixed(4)}`;
      const site = await createSite({
        name, lat: pin.lat, lon: pin.lon,
        category: category || null, radius_km: radiusKm,
      });
      refreshSites();
      setSelSiteId(site?.id || null);
      showToast('Saved to pipeline');
    } catch (err) {
      showToast(err.message || 'Could not save site');
    }
  };

  const handleSelectSite = (site) => {
    setSelSiteId(site.id);
    if (site.category && site.category !== 'shopping_centre') setCategory(site.category);
    if (site.radius_km) setRadiusKm(site.radius_km);
    setPin({ lat: site.lat, lon: site.lon });
  };

  const handleViewReport = (reportData, requestData, loc) => {
    setOverlay(null);
    // Feasibility reports open as the fullscreen study; retail goes to the panel.
    if (reportData?.feasibility) {
      setFeasFull({ result: reportData, request: requestData, location: loc });
      return;
    }
    skipScoreRef.current = true;   // don't auto-re-score the pin we're loading
    if (loc) setPin(loc);
    if (requestData?.category && requestData.category !== 'shopping_centre') {
      setCategory(requestData.category);
    }
    if (requestData?.radius_km) setRadiusKm(requestData.radius_km);
    setResult(reportData);
  };

  const handleDownloadPdf = async () => {
    if (!result?.report_id) return;
    setPdfBusy(true);
    try {
      await downloadReportPdf(result.report_id);
    } catch {
      showToast('PDF generation failed — try again shortly');
    } finally {
      setPdfBusy(false);
    }
  };

  const handleDemo = () => {
    skipScoreRef.current = true;
    setCategory(MOCK_RESULT.category);
    setResult(MOCK_RESULT);
  };

  const hasQR = !userProducts || userProducts.includes('quick_report');
  const hasSC = !userProducts || userProducts.includes('sc_feasibility');
  const effectiveMode = hasQR ? mode : 'feasibility';

  // Map dots: in feasibility mode show competing centres; in retail show
  // category POI preview plus the user's own-brand stores.
  const feasCentres = feasResult?.competing_centres || feasResult?.feasibility?.competing_centres || [];
  const mapDots = effectiveMode === 'feasibility'
    ? feasCentres.map(c => ({ ...c, brand: c.name, threat: 'Centre' }))
    : [...mapPois, ...brandLocations.map(l => ({ ...l, threat: 'Own Brand' }))];

  const initials = (userEmail || '??').slice(0, 2).toUpperCase();
  const navItems = isAdmin ? [...OVERLAYS, { key: 'admin', label: 'Admin' }] : OVERLAYS;

  return (
    <LoginGate>
      <MapPanel
        onLocationSelect={handleMapClick}
        location={pin}
        radiusKm={radiusKm}
        competitors={mapDots}
      />

      {/* ---------- header ---------- */}
      <header className="sq-glass" style={isMobile ? hStyles.headerMobile : hStyles.header}>
        <div style={hStyles.brand}>Site<b style={{ color: 'var(--s-accent)' }}>IQ</b></div>
        {!isMobile && (
          <nav style={hStyles.nav}>
            {navItems.map(o => (
              <button key={o.key} onClick={() => setOverlay(o.key)} style={hStyles.navBtn}>
                {o.label}
              </button>
            ))}
          </nav>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setShowPalette(true)}
          style={isMobile ? hStyles.searchIcon : hStyles.search}
          title="Search — ⌘K"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          {!isMobile && <><span style={{ flex: 1, textAlign: 'left' }}>Search…</span><kbd style={hStyles.kbd}>⌘K</kbd></>}
        </button>
        <button
          onClick={() => { toggleTheme(); setThemeMode(getTheme()); }}
          style={hStyles.themeBtn}
          title={themeMode === 'light' ? 'Switch to dark' : 'Switch to light'}
          aria-label="Toggle light or dark theme"
        >
          {themeMode === 'light' ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
            </svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
            </svg>
          )}
        </button>
        <button
          onClick={() => setShowPurchase(true)}
          style={hStyles.credits}
          title="Credits — click to buy more"
        >
          <span style={{ color: 'var(--s-accent)', fontWeight: 700 }}>
            {(effectiveMode === 'feasibility' ? scCredits : credits) ?? '—'}
          </span>
          &nbsp;{effectiveMode === 'feasibility' ? 'SC credits' : 'credits'}
        </button>
        <button onClick={() => setOverlay('account')} style={hStyles.avatar} title={userEmail || ''}>
          {initials}
        </button>
      </header>

      {/* ---------- pipeline rail ---------- */}
      {!isMobile && (
        <Rail
          sites={sites}
          selectedId={selSiteId}
          onSelect={handleSelectSite}
          onSave={handleSaveSite}
          canSave={!!pin}
          onOpenPipeline={() => setOverlay('pipeline')}
        />
      )}

      {/* ---------- insight panel ---------- */}
      <aside className="sq-glass" style={isMobile ? hStyles.insightMobile : hStyles.insight}>
        {effectiveMode === 'quick' && (
          <InsightPanel
            result={result}
            loading={loading}
            error={error}
            hasPin={!!pin}
            category={category}
            radiusKm={radiusKm}
            capture={capture}
            credits={credits}
            isAdmin={isAdmin}
            onUnlock={handleUnlock}
            unlocking={unlocking}
            onBuyCredits={() => setShowPurchase(true)}
            onDemo={!result ? handleDemo : null}
            onDownloadPdf={handleDownloadPdf}
            pdfBusy={pdfBusy}
          />
        )}
        {effectiveMode === 'feasibility' && (
          <FeasibilityInsightPanel
            result={feasResult}
            loading={feasLoading}
            error={feasError}
            hasPin={!!pin}
            radiusKm={radiusKm}
            credits={scCredits}
            isAdmin={isAdmin}
            onUnlock={handleFeasUnlock}
            unlocking={feasUnlocking}
            onBuyCredits={() => setShowPurchase(true)}
            onRun={handleFeasibilityRun}
            onViewFull={() => setFeasFull({
              result: feasResult,
              request: { category: 'shopping_centre', radius_km: radiusKm },
              location: pin,
            })}
          />
        )}
      </aside>

      {/* ---------- scenario dock ---------- */}
      <ScenarioDock
        mode={effectiveMode}
        onMode={(m) => {
          // Shopping-centre trade areas are larger — bump the default once.
          if (m === 'feasibility' && radiusKm < 5) setRadiusKm(5);
          if (m === 'quick' && radiusKm > 5) setRadiusKm(3);
          setMode(m);
        }}
        showModeToggle={hasQR && hasSC}
        category={category}
        onCategory={(c) => { setCategory(c); }}
        allowedCategories={userCategories}
        radiusKm={radiusKm}
        onRadius={setRadiusKm}
        capture={capture}
        onCapture={setCapture}
        isMobile={isMobile}
      />

      {/* ---------- feasibility study (fullscreen) ---------- */}
      {feasFull && (
        <div style={hStyles.feasWrap}>
          <FeasibilityReportPanel
            result={feasFull.result}
            request={feasFull.request}
            location={feasFull.location}
            onBack={() => setFeasFull(null)}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* ---------- overlays ---------- */}
      {overlay && (
        <div style={hStyles.overlayBg} onClick={(e) => { if (e.target === e.currentTarget) setOverlay(null); }}>
          <div className="sq-overlay-card" style={hStyles.overlayCard}>
            <button onClick={() => setOverlay(null)} style={hStyles.overlayClose}>✕</button>
            <div style={hStyles.overlayBody}>
              {overlay === 'pipeline' && (
                <PipelinePanel
                  onViewReport={handleViewReport}
                  onRunReport={(site) => { setOverlay(null); handleSelectSite(site); }}
                  onManagePortfolio={() => { setOverlay(null); setShowPortfolio(true); }}
                  isMobile={isMobile}
                />
              )}
              {overlay === 'reports' && (
                <MyReportsPanel onViewReport={handleViewReport} isMobile={isMobile} />
              )}
              {overlay === 'predictive' && <PredictivePanel />}
              {overlay === 'invoices' && <InvoicesPanel isMobile={isMobile} />}
              {overlay === 'admin' && isAdmin && <AdminPanel />}
              {overlay === 'account' && (
                <AccountPanel isMobile={isMobile} onPurchase={() => { setOverlay(null); setShowPurchase(true); }} />
              )}
            </div>
          </div>
        </div>
      )}

      {showPortfolio && (
        <PortfolioSetup
          onComplete={() => setShowPortfolio(false)}
          onClose={() => setShowPortfolio(false)}
        />
      )}

      {showPurchase && (
        <PurchaseModal
          onClose={() => setShowPurchase(false)}
          onComplete={() => {
            getCredits().then(d => setCredits(d.credits)).catch(() => {});
          }}
        />
      )}

      {/* ---------- mobile nav ---------- */}
      {isMobile && (
        <div style={hStyles.mobileNav}>
          {navItems.map(o => (
            <button key={o.key} onClick={() => setOverlay(o.key)} style={hStyles.mobileNavBtn}>
              {o.label}
            </button>
          ))}
        </div>
      )}

      {/* ---------- command palette ---------- */}
      <CommandPalette
        open={showPalette}
        onClose={() => setShowPalette(false)}
        sites={sites}
        categories={userCategories?.length ? CATEGORIES.filter(c => userCategories.includes(c.key)) : CATEGORIES}
        mode={effectiveMode}
        hasQR={hasQR}
        hasSC={hasSC}
        isAdmin={isAdmin}
        hasLockedResult={effectiveMode === 'quick' && !!result?.locked}
        canSave={!!pin}
        onJumpSite={handleSelectSite}
        onCategory={setCategory}
        onMode={(m) => { if (m === 'feasibility' && radiusKm < 5) setRadiusKm(5); if (m === 'quick' && radiusKm > 5) setRadiusKm(3); setMode(m); }}
        onOverlay={setOverlay}
        onSave={handleSaveSite}
        onBuyCredits={() => setShowPurchase(true)}
        onDemo={(effectiveMode === 'quick' && !result) ? handleDemo : null}
        onUnlock={handleUnlock}
      />

      {/* ---------- toast ---------- */}
      {toast && <div style={hStyles.toast}>{toast}</div>}
    </LoginGate>
  );
}

const hStyles = {
  header: {
    position: 'fixed',
    top: 18, left: 18, right: 18,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '10px 16px',
  },
  headerMobile: {
    position: 'fixed',
    top: 10, left: 10, right: 10,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
  },
  brand: {
    fontFamily: 'var(--s-serif)',
    fontSize: 19,
    fontWeight: 700,
    color: 'var(--s-text)',
    whiteSpace: 'nowrap',
  },
  nav: { display: 'flex', gap: 2, marginLeft: 6 },
  navBtn: {
    appearance: 'none',
    border: 0,
    background: 'transparent',
    color: 'var(--s-muted)',
    font: '600 12px var(--s-sans)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '8px 12px',
    borderRadius: 9,
    cursor: 'pointer',
  },
  search: {
    display: 'flex', alignItems: 'center', gap: 9,
    width: 200,
    border: '1px solid var(--s-line)',
    background: 'var(--s-w04)',
    color: 'var(--s-muted)',
    font: '500 13px var(--s-sans)',
    padding: '8px 12px',
    borderRadius: 10,
    cursor: 'pointer',
  },
  searchIcon: {
    display: 'grid', placeItems: 'center',
    width: 34, height: 34,
    border: '1px solid var(--s-line)',
    background: 'var(--s-w04)',
    color: 'var(--s-muted)',
    borderRadius: 10,
    cursor: 'pointer',
  },
  kbd: {
    font: '600 10px var(--s-mono)', color: 'var(--s-faint)',
    border: '1px solid var(--s-line)', borderRadius: 5, padding: '2px 6px',
    background: 'rgba(0,0,0,0.3)',
  },
  credits: {
    appearance: 'none',
    border: '1px solid var(--s-line)',
    background: 'var(--s-w04)',
    color: 'var(--s-muted)',
    font: '600 12px var(--s-mono)',
    padding: '7px 12px',
    borderRadius: 99,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  themeBtn: {
    appearance: 'none',
    border: '1px solid var(--s-line)',
    background: 'var(--s-w04)',
    color: 'var(--s-muted)',
    width: 32, height: 32,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
  },
  avatar: {
    appearance: 'none',
    border: 'none',
    width: 32, height: 32,
    borderRadius: '50%',
    flexShrink: 0,
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #ff6a3d, #a23bff)',
    color: '#0b0c0f',
    font: '700 11px var(--s-sans)',
  },
  insight: {
    position: 'fixed',
    right: 18, top: 84, bottom: 18,
    width: 392,
    zIndex: 15,
    overflowY: 'auto',
  },
  insightMobile: {
    position: 'fixed',
    left: 0, right: 0, bottom: 0,
    height: '52dvh',
    zIndex: 15,
    overflowY: 'auto',
    borderRadius: '16px 16px 0 0',
  },
  feasPrompt: { padding: '26px' },
  feasWrap: { position: 'fixed', inset: 0, zIndex: 40, background: 'var(--s-bg)', overflowY: 'auto' },
  overlayBg: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    background: 'rgba(5,6,8,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  overlayCard: {
    position: 'relative',
    width: 'min(1080px, 96vw)',
    height: 'min(760px, 92vh)',
    display: 'flex',
    flexDirection: 'column',
  },
  overlayClose: {
    position: 'absolute',
    top: 12, right: 12,
    zIndex: 5,
    appearance: 'none',
    border: 'none',
    width: 30, height: 30,
    borderRadius: 8,
    background: 'var(--s-w08)',
    color: 'var(--s-muted)',
    cursor: 'pointer',
    fontSize: 14,
  },
  overlayBody: { flex: 1, overflowY: 'auto' },
  mobileNav: {
    position: 'fixed',
    top: 62, left: 10,
    zIndex: 18,
    display: 'flex',
    gap: 6,
  },
  mobileNavBtn: {
    appearance: 'none',
    border: '1px solid var(--s-line)',
    background: 'var(--s-panel)',
    color: 'var(--s-muted)',
    font: '600 11px var(--s-sans)',
    padding: '6px 11px',
    borderRadius: 99,
    cursor: 'pointer',
    backdropFilter: 'blur(12px)',
  },
  toast: {
    position: 'fixed',
    bottom: 26,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 70,
    background: '#1c1d23',
    border: '1px solid var(--s-line)',
    color: 'var(--s-text)',
    fontSize: 13,
    padding: '11px 18px',
    borderRadius: 11,
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
  },
};
